import React from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Dashboard from './pages/Dashboard';
import Cities from './pages/Cities';
import CityDetail from './pages/CityDetail';
import Meetups from './pages/Meetups';
import Discover from './pages/Discover';
import Matches from './pages/Matches';
import Chat from './pages/Chat';
import TimeStayListings from './pages/TimeStayListings';
import Posts from './pages/Posts';
import PostDetail from './pages/PostDetail';
import CreatePost from './pages/CreatePost';
import Profile from './pages/Profile';

const navStyle: React.CSSProperties = {
  backgroundColor: '#000',
  padding: '10px 20px',
  borderBottom: '1px solid #333',
  position: 'sticky',
  top: 0,
  zIndex: 100,
};

const containerStyle: React.CSSProperties = {
  maxWidth: '1200px',
  margin: '0 auto',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const linkStyle = (active: boolean): React.CSSProperties => ({
  color: active ? '#4ade80' : '#fff',
  textDecoration: 'none',
  padding: '8px 12px',
  borderRadius: '4px',
  fontSize: '14px',
  borderBottom: active ? '2px solid #4ade80' : 'none',
});

function Navigation() {
  const location = useLocation();
  const auth = useAuth();

  const links = [
    { to: '/', label: 'Dashboard' },
    { to: '/cities', label: 'Cities' },
    { to: '/meetups', label: 'Meetups' },
    { to: '/discover', label: 'Discover' },
    { to: '/timestay', label: 'TimeStay' },
    { to: '/posts', label: 'Posts' },
  ];

  return (
    <nav style={navStyle}>
      <div style={containerStyle}>
        <Link to="/" style={{ color: '#fff', textDecoration: 'none', fontSize: '1.25rem', fontWeight: 'bold' }}>
          Georgii.tech
        </Link>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {links.map(({ to, label }) => (
            <Link key={to} to={to} style={linkStyle(location.pathname === to || (to !== '/' && location.pathname.startsWith(to)))}>
              {label}
            </Link>
          ))}
          <Link to="/profile" style={linkStyle(location.pathname === '/profile')}>
            {auth.user?.profile?.email ?? 'Profile'}
          </Link>
          <button
            onClick={() => auth.signoutRedirect()}
            style={{
              backgroundColor: '#374151',
              color: '#fff',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </nav>
  );
}

function LoadingScreen() {
  return (
    <div style={{ backgroundColor: '#1a1a1a', color: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '50px', height: '50px', border: '3px solid #374151', borderTopColor: '#4ade80', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <p style={{ marginTop: '20px' }}>Loading…</p>
    </div>
  );
}

function AuthErrorScreen() {
  const auth = useAuth();
  return (
    <div style={{ backgroundColor: '#1a1a1a', color: '#fff', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', padding: '30px', border: '1px solid #dc2626', borderRadius: '12px', maxWidth: '500px' }}>
        <h2 style={{ color: '#dc2626', marginBottom: '15px' }}>Authentication Error</h2>
        <p style={{ color: '#9ca3af', marginBottom: '20px' }}>{auth.error?.message}</p>
        <button
          onClick={() => auth.signinRedirect()}
          style={{ backgroundColor: '#4ade80', color: '#000', padding: '12px 24px', fontWeight: 600, border: 'none', borderRadius: '8px', cursor: 'pointer' }}
        >
          Sign in again
        </button>
      </div>
    </div>
  );
}

function RedirectToLogin() {
  const auth = useAuth();
  auth.signinRedirect();
  return (
    <div style={{ backgroundColor: '#1a1a1a', color: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <p>Redirecting to login…</p>
    </div>
  );
}

function AppRoutes() {
  const auth = useAuth();

  if (auth.isLoading) return <LoadingScreen />;
  if (auth.error) return <AuthErrorScreen />;
  if (!auth.isAuthenticated) return <RedirectToLogin />;

  return (
    <div style={{ backgroundColor: '#1a1a1a', minHeight: '100vh' }}>
      <Navigation />
      <main style={{ paddingBottom: '40px' }}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/cities" element={<Cities />} />
          <Route path="/cities/:cityId" element={<CityDetail />} />
          <Route path="/meetups" element={<Meetups />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/matches" element={<Matches />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/timestay" element={<TimeStayListings />} />
          <Route path="/posts" element={<Posts />} />
          <Route path="/posts/new" element={<CreatePost />} />
          <Route path="/posts/:postId" element={<PostDetail />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

const App: React.FC = () => (
  <BrowserRouter>
    <AppRoutes />
  </BrowserRouter>
);

export default App;
